import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, DollarSign, TrendingUp, BarChart3, Shield, CheckCircle,
  ArrowRight, Building2, Mail, ChevronDown, Zap, Clock, Users, User, CreditCard, Loader
} from 'lucide-react';
import { mpService } from '../services/api';

/* ─── Intersection Observer hook ─── */
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ─── Animated counter ─── */
function Counter({ to, suffix = '', duration = 1800 }) {
  const [val, setVal] = useState(0);
  const [ref, visible] = useReveal(0.3);
  useEffect(() => {
    if (!visible) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      setVal(Math.floor(eased * to));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [visible, to, duration]);
  return <span ref={ref}>{val.toLocaleString('es-AR')}{suffix}</span>;
}

/* ─── Reveal wrapper ─── */
function Reveal({ children, delay = 0, className = '', direction = 'up' }) {
  const [ref, visible] = useReveal();
  const transforms = {
    up: 'translateY(40px)',
    down: 'translateY(-40px)',
    left: 'translateX(40px)',
    right: 'translateX(-40px)',
    scale: 'scale(0.92)',
  };
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translate(0) scale(1)' : transforms[direction],
        transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/* ─── Floating particles for hero ─── */
function FloatingParticles() {
  const particles = useMemo(() =>
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 15 + 10,
      delay: Math.random() * 5,
      opacity: Math.random() * 0.3 + 0.05,
    }))
  , []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full bg-[#ed6f28]"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            opacity: p.opacity,
            animation: `particleFloat ${p.duration}s ease-in-out ${p.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

/* ─── Magnetic tilt on hover for cards ─── */
function TiltCard({ children, className = '' }) {
  const ref = useRef(null);
  const handleMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.transform = `perspective(600px) rotateY(${x * 6}deg) rotateX(${-y * 6}deg) scale(1.02)`;
  }, []);
  const handleLeave = useCallback(() => {
    const el = ref.current;
    if (el) el.style.transform = 'perspective(600px) rotateY(0) rotateX(0) scale(1)';
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ transition: 'transform 0.3s ease' }}
    >
      {children}
    </div>
  );
}

/* ─── WhatsApp SVG icon ─── */
function WhatsAppIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

/* ─── Data ─── */
const features = [
  {
    icon: FileText,
    title: 'Contratos digitales',
    desc: 'Creá y administrá contratos de alquiler con toda la información del inquilino, propietario y condiciones legales.',
  },
  {
    icon: TrendingUp,
    title: 'Actualizaciones ICL / IPC',
    desc: 'Calculá incrementos según el índice oficial del BCRA (Ley 27.551) de forma automática y sin errores.',
  },
  {
    icon: DollarSign,
    title: 'Seguimiento de pagos',
    desc: 'Registrá cobros, identificá deudores y enviá notificaciones por WhatsApp o correo electrónico.',
  },
  {
    icon: BarChart3,
    title: 'Reportes para propietarios',
    desc: 'Generá resúmenes mensuales en PDF para cada propietario con ingresos, egresos y estado de pagos.',
  },
  {
    icon: Shield,
    title: 'Datos aislados y seguros',
    desc: 'Cada cuenta opera en un entorno completamente independiente. Tu información es estrictamente privada.',
  },
  {
    icon: Zap,
    title: 'Recibos y boletas en PDF',
    desc: 'Emitís comprobantes de pago y boletas de servicios con tu logo institucional en segundos.',
  },
];

const steps = [
  { number: '01', title: 'Creá tu cuenta', desc: 'Registrate en menos de un minuto. Sin tarjeta, sin compromiso.' },
  { number: '02', title: 'Cargá tus contratos', desc: 'Ingresá los datos de propietarios, inquilinos y condiciones del alquiler.' },
  { number: '03', title: 'Gestioná todo desde un lugar', desc: 'Pagos, aumentos, reportes y recibos. Todo automatizado.' },
];

const plans = [
  {
    code: 'TRIAL',
    name: 'Período de prueba',
    price: 'Sin costo',
    period: '2 meses',
    highlight: false,
    badge: null,
    features: [
      'Acceso completo a todas las funciones',
      'Contratos ilimitados',
      'Sin tarjeta de crédito requerida',
    ],
  },
  {
    code: 'MENSUAL',
    name: 'Mensual',
    price: '$35.000',
    rawPrice: 35000,
    period: 'por mes',
    highlight: false,
    badge: null,
    features: [
      'Contratos ilimitados',
      'Actualizaciones ICL / IPC',
      'Reportes en PDF',
      'Soporte por correo',
    ],
  },
  {
    code: 'SEMESTRAL',
    name: 'Semestral',
    price: '$180.000',
    rawPrice: 180000,
    period: '6 meses',
    highlight: true,
    badge: '14% de descuento',
    features: [
      'Todo lo incluido en el plan mensual',
      'Equivale a $30.000 / mes',
      'Prioridad en soporte técnico',
    ],
  },
  {
    code: 'ANUAL',
    name: 'Anual',
    price: '$330.000',
    rawPrice: 330000,
    period: 'por año',
    highlight: false,
    badge: '21% de descuento',
    features: [
      'Todo lo incluido en el plan mensual',
      'Equivale a $27.500 / mes',
      'Soporte con respuesta prioritaria',
    ],
  },
];

const stats = [
  { value: 60, suffix: ' días', label: 'de prueba gratuita' },
  { value: 100, suffix: '%', label: 'de datos privados por cuenta' },
  { value: 24, suffix: ' hs', label: 'actualizaciones de índices' },
];

/* ─── Confirmation modal before MP redirect ─── */
function ConfirmPagoModal({ plan, userEmail, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 max-w-sm w-full shadow-2xl">
        <div className="flex items-center justify-center w-12 h-12 bg-[#ed6f28]/10 rounded-full mb-5 mx-auto">
          <CreditCard className="w-6 h-6 text-[#ed6f28]" />
        </div>
        <h3 className="text-white text-lg font-bold text-center mb-2">Confirmar suscripción</h3>
        <p className="text-gray-400 text-sm text-center mb-6">
          Vas a suscribirte al <span className="text-white font-semibold">Plan {plan.plan_name}</span> por{' '}
          <span className="text-white font-semibold">${Number(plan.price).toLocaleString('es-AR')} ARS</span>.
        </p>
        <div className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 mb-6">
          <p className="text-gray-400 text-xs mb-1">La suscripción se aplicará a la cuenta</p>
          <p className="text-white text-sm font-medium truncate">{userEmail}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-[#ed6f28] hover:bg-[#c45e1e] text-white transition-colors"
          >
            Ir a pagar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Page ─── */
export default function InfoContacto() {
  const navigate = useNavigate();

  const isLoggedIn = !!localStorage.getItem('token');
  const storedUser = isLoggedIn ? JSON.parse(localStorage.getItem('user') || '{}') : null;
  const userEmail = storedUser?.email || '';
  const currentPlanCode = storedUser?.plan_code || null;

  const [payingPlan, setPayingPlan] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    if (!isLoggedIn) return;
    const pendingPlan = sessionStorage.getItem('pendingPlan');
    if (!pendingPlan) return;
    sessionStorage.removeItem('pendingPlan');
    handleSuscribirse(pendingPlan);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSuscribirse = async (planCode) => {
    if (!isLoggedIn) {
      sessionStorage.setItem('pendingPlan', planCode);
      navigate('/app');
      return;
    }

    const planInfo = plans.find(p => p.code === planCode);
    setConfirmModal({
      planCode,
      plan_name: planInfo?.name || planCode,
      price: planInfo?.rawPrice || 0,
    });
  };

  const confirmarPago = async () => {
    const planCode = confirmModal.planCode;
    setConfirmModal(null);

    try {
      setPayingPlan(planCode);
      const data = await mpService.crearPreferencia(planCode);

      if (!data.success) {
        alert('No se pudo iniciar el pago. ' + (data.error || 'Intentá de nuevo.'));
        return;
      }

      const url = data.init_point || data.sandbox_init_point;
      window.location.href = url;

    } catch (err) {
      console.error('Error al iniciar pago:', err);
      alert('Error al iniciar el pago: ' + (err.message || 'Intentá de nuevo.'));
    } finally {
      setPayingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white font-sans">

      {/* ── MODAL CONFIRMACIÓN PAGO ── */}
      {confirmModal && (
        <ConfirmPagoModal
          plan={confirmModal}
          userEmail={userEmail}
          onConfirm={confirmarPago}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {/* ── NAV ── */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-gray-950/80 backdrop-blur-md border-b border-white/5" style={{ animation: 'heroFadeUp 0.6s cubic-bezier(0.16,1,0.3,1) both' }}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <img
            src="/logorentiacompletonaranjablanco.png"
            alt="Rentia"
            className="h-9 w-auto object-contain cursor-pointer"
            onClick={() => navigate('/')}
          />
          <div className="flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <button
                  onClick={() => navigate('/app')}
                  className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-medium px-3 py-1.5 rounded-full transition-colors"
                >
                  <span className="w-7 h-7 bg-[#ed6f28] rounded-full flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </span>
                  <span className="hidden sm:block truncate max-w-[140px]">{storedUser?.nombre || storedUser?.email}</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/app')}
                  className="text-gray-400 hover:text-white text-sm px-4 py-2 rounded-lg transition-colors"
                >
                  Iniciar sesión
                </button>
                <button
                  onClick={() => navigate('/registro')}
                  className="bg-[#ed6f28] hover:bg-[#c45e1e] text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors"
                >
                  Comenzar gratis
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-16 overflow-hidden">
        {/* Dot grid background */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }} />
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950" style={{ opacity: 0.8 }} />
        {/* Animated gradient orb */}
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, #ed6f28 0%, transparent 70%)', animation: 'orbPulse 6s ease-in-out infinite', opacity: 0.08 }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, #ed6f28 0%, transparent 70%)', animation: 'orbPulse 8s ease-in-out 2s infinite', opacity: 0.04 }}
        />
        <FloatingParticles />

        <div className="relative max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div style={{ animation: 'heroFadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.1s both' }}>
            <span className="inline-block border border-[#ed6f28]/40 text-[#ed6f28] text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-8 hero-badge-glow">
              2 meses gratuitos · Sin tarjeta de crédito
            </span>
          </div>
          {/* Title */}
          <div style={{ animation: 'heroFadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.25s both' }}>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6">
              Administración de{' '}
              <span className="hero-outline-text">alquileres</span>
              <br />sin fricciones
            </h1>
          </div>
          {/* Subtitle */}
          <div style={{ animation: 'heroFadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.4s both' }}>
            <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
              La plataforma para inmobiliarias y administradores que necesitan
              centralizar contratos, pagos e índices de actualización en un solo lugar.
            </p>
          </div>
          {/* Buttons */}
          <div style={{ animation: 'heroFadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.55s both' }}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/registro')}
                className="group relative inline-flex items-center justify-center gap-2 bg-[#ed6f28] hover:bg-[#c45e1e] text-white font-semibold px-8 py-4 rounded-xl text-base transition-all shadow-2xl shadow-[#ed6f28]/20 hover:shadow-[#ed6f28]/40 hover:scale-[1.03] active:scale-[0.98]"
              >
                Comenzar período de prueba
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => {
                  document.getElementById('precios')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center justify-center gap-2 border border-white/10 hover:border-white/25 text-gray-300 hover:text-white font-semibold px-8 py-4 rounded-xl text-base transition-all hover:scale-[1.03] active:scale-[0.98]"
              >
                Ver planes
                <ChevronDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" style={{ animation: 'heroFadeUp 1s ease 1.2s both' }}>
          <span className="text-gray-400 text-[10px] uppercase tracking-widest">Scroll</span>
          <div className="w-5 h-8 rounded-full border border-gray-600 flex justify-center pt-1.5">
            <div className="w-1 h-1.5 rounded-full bg-gray-400" style={{ animation: 'scrollDot 2s ease-in-out infinite' }} />
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="py-20 border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-0">
            {stats.map((s, i) => (
              <Reveal key={i} delay={i * 150} direction="scale">
                <div className={`stat-item group text-center py-8 sm:py-0 ${i < stats.length - 1 ? 'sm:border-r border-b sm:border-b-0 border-white/[0.06]' : ''}`}>
                  <p className="text-5xl font-bold tabular-nums stat-number">
                    <Counter to={s.value} suffix={s.suffix} />
                  </p>
                  <p className="text-gray-400 text-sm mt-3 uppercase tracking-wider">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="text-[#ed6f28] text-xs font-semibold tracking-widest uppercase text-center mb-4">
              Funcionalidades
            </p>
            <h2 className="text-4xl font-bold text-center mb-3">Todo lo que necesitás</h2>
            <p className="text-gray-400 text-center mb-16 max-w-xl mx-auto">
              Diseñado específicamente para el mercado de alquileres en Argentina.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={i} delay={i * 100} direction={i % 2 === 0 ? 'up' : 'scale'}>
                  <TiltCard className="group h-full bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 hover:border-[#ed6f28]/30 hover:bg-white/[0.05] transition-all duration-500 hover:shadow-lg hover:shadow-[#ed6f28]/5">
                    <div className="w-10 h-10 rounded-xl bg-[#ed6f28]/10 flex items-center justify-center mb-5 group-hover:bg-[#ed6f28]/20 transition-all duration-500 group-hover:scale-110">
                      <Icon className="w-5 h-5 text-[#ed6f28] transition-transform duration-500 group-hover:rotate-[-8deg]" />
                    </div>
                    <h3 className="text-white font-semibold text-base mb-2">{f.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                  </TiltCard>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-28 px-6 border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="text-[#ed6f28] text-xs font-semibold tracking-widest uppercase text-center mb-4">
              Cómo funciona
            </p>
            <h2 className="text-4xl font-bold text-center mb-16">Empezá en tres pasos</h2>
          </Reveal>

          <div className="relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-10 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ed6f28]/20 to-transparent" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
              {steps.map((step, i) => (
                <Reveal key={i} delay={i * 150} direction="up">
                  <div className="text-center group">
                    <div className="relative z-10 inline-flex items-center justify-center w-20 h-20 rounded-full border-2 border-[#ed6f28]/40 bg-gray-950 mb-6 group-hover:border-[#ed6f28] transition-all duration-500 group-hover:shadow-[0_0_20px_rgba(237,111,40,0.2)]">
                      <span className="text-2xl font-bold text-[#ed6f28]/70 group-hover:text-[#ed6f28] transition-colors">{step.number}</span>
                    </div>
                    <h3 className="text-white font-semibold text-lg mb-2">{step.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="precios" className="py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="text-[#ed6f28] text-xs font-semibold tracking-widest uppercase text-center mb-4">
              Planes y precios
            </p>
            <h2 className="text-4xl font-bold text-center mb-3">Transparente desde el primer día</h2>
            <p className="text-gray-400 text-center mb-16 max-w-xl mx-auto">
              Comenzá sin compromiso. Pasá a un plan pago solo si Rentia te convence.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {plans.map((plan, i) => {
              const isCurrent = isLoggedIn && currentPlanCode === plan.code;
              return (
              <Reveal key={plan.code} delay={i * 100}>
                <div
                  className={`relative flex flex-col h-full rounded-2xl p-6 border transition-all duration-500 hover:translate-y-[-4px] ${
                    isCurrent
                      ? 'bg-[#ed6f28]/8 border-[#ed6f28]/60 shadow-xl shadow-[#ed6f28]/10'
                      : plan.highlight
                        ? 'bg-[#ed6f28]/8 border-[#ed6f28]/50 shadow-xl shadow-[#ed6f28]/10 plan-shimmer'
                        : 'bg-white/[0.03] border-white/[0.07] hover:border-white/20 hover:shadow-lg hover:shadow-white/[0.02]'
                  }`}
                >
                  {isCurrent && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ed6f28] text-white text-[10px] font-bold tracking-wider uppercase px-3 py-1 rounded-full whitespace-nowrap">
                      Plan actual
                    </span>
                  )}
                  {!isCurrent && plan.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ed6f28] text-white text-[10px] font-bold tracking-wider uppercase px-3 py-1 rounded-full whitespace-nowrap">
                      {plan.badge}
                    </span>
                  )}
                  <div className="mb-6">
                    <p className="text-gray-300 text-xs uppercase tracking-widest mb-3">{plan.name}</p>
                    <p className="text-3xl font-bold text-white">{plan.price}</p>
                    <p className="text-gray-400 text-xs mt-1">{plan.period}</p>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((feat, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm text-gray-300">
                        <CheckCircle className="w-4 h-4 text-[#ed6f28] flex-shrink-0 mt-0.5" />
                        {feat}
                      </li>
                    ))}
                  </ul>
                  {plan.code === 'TRIAL' ? (
                    isLoggedIn ? (
                      <div className="w-full py-2.5 rounded-lg text-sm text-center text-gray-400 border border-white/5">
                        Sin costo
                      </div>
                    ) : (
                      <button
                        onClick={() => navigate('/registro')}
                        className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/10"
                      >
                        Comenzar sin costo
                      </button>
                    )
                  ) : isCurrent ? (
                    <div className="w-full py-2.5 rounded-lg text-sm text-center font-semibold text-[#ed6f28] border border-[#ed6f28]/30 bg-[#ed6f28]/5">
                      Plan vigente
                    </div>
                  ) : isLoggedIn ? (
                    <button
                      onClick={() => handleSuscribirse(plan.code)}
                      disabled={payingPlan === plan.code}
                      className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                        plan.highlight
                          ? 'bg-[#ed6f28] hover:bg-[#c45e1e] text-white disabled:opacity-60'
                          : 'bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/10 disabled:opacity-60'
                      }`}
                    >
                      {payingPlan === plan.code ? (
                        <><Loader className="w-4 h-4 animate-spin" /> Procesando...</>
                      ) : (
                        <><CreditCard className="w-4 h-4" /> Pagar con MercadoPago</>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSuscribirse(plan.code)}
                      className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        plan.highlight
                          ? 'bg-[#ed6f28] hover:bg-[#c45e1e] text-white'
                          : 'bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/10'
                      }`}
                    >
                      Seleccionar plan
                    </button>
                  )}
                </div>
              </Reveal>
              );
            })}
          </div>
          <Reveal delay={200}>
            <p className="text-center text-gray-400 text-xs mt-10">
              Precios en pesos argentinos · IVA incluido · Cancelación en cualquier momento
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── WHY ── */}
      <section className="py-28 px-6 border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="text-[#ed6f28] text-xs font-semibold tracking-widest uppercase text-center mb-4">
              Por qué Rentia
            </p>
            <h2 className="text-4xl font-bold text-center mb-16">
              Construido para el mercado argentino
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Clock, title: 'Implementación inmediata', desc: 'Sin instalaciones ni configuraciones complejas. Creá tu cuenta y comenzá a operar en minutos.' },
              { icon: Users, title: 'Multi-propietario', desc: 'Gestioná propiedades de múltiples propietarios desde una sola cuenta, con reportes individualizados.' },
              { icon: Building2, title: 'Actualización legal', desc: 'Cumplí con la Ley 27.551 automáticamente. Los índices ICL e IPC se actualizan desde el BCRA e INDEC.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal key={i} delay={i * 150} direction={i === 1 ? 'up' : i === 0 ? 'right' : 'left'}>
                  <div className="group text-center">
                    <div className="w-12 h-12 rounded-2xl bg-[#ed6f28]/10 flex items-center justify-center mx-auto mb-5 transition-all duration-500 group-hover:bg-[#ed6f28]/20 group-hover:scale-110 group-hover:rounded-xl">
                      <Icon className="w-6 h-6 text-[#ed6f28] transition-transform duration-500 group-hover:scale-110" />
                    </div>
                    <h3 className="text-white font-semibold text-base mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-28 px-6 relative overflow-hidden">
        {/* Orb de fondo */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, #ed6f28 0%, transparent 70%)', opacity: 0.07 }}
        />
        <Reveal direction="scale">
          <div className="max-w-2xl mx-auto text-center relative">
            <h2 className="text-4xl md:text-5xl font-bold mb-5 leading-tight text-white">
              Comenzá a ordenar<br />tu cartera hoy
            </h2>
            <p className="text-gray-300 mb-10 text-lg">
              Dos meses completos sin cargo. Sin límites. Sin tarjeta.
            </p>
            <div className="relative inline-block">
              <div className="absolute -inset-1 bg-[#ed6f28]/25 rounded-xl blur-xl" />
              <button
                onClick={() => navigate('/registro')}
                className="relative group inline-flex items-center gap-2 bg-[#ed6f28] hover:bg-[#c45e1e] text-white font-bold px-10 py-4 rounded-xl text-base transition-all shadow-2xl shadow-[#ed6f28]/20 hover:shadow-[#ed6f28]/40 hover:scale-[1.03] active:scale-[0.98]"
              >
                Empezar gratis
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ── CONTACT ── */}
      <section className="py-16 px-6 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="text-[#ed6f28] text-xs font-semibold tracking-widest uppercase text-center mb-10">
              Contacto
            </p>
          </Reveal>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-10 sm:gap-16 max-w-2xl mx-auto">
            <Reveal delay={0}>
              <a
                href="mailto:soporte@rentiapropiedades.com"
                className="flex items-center gap-3 group"
              >
                <Mail className="w-5 h-5 text-[#ed6f28] flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-0.5">Correo</p>
                  <p className="text-gray-200 text-sm font-medium group-hover:text-white transition-colors truncate">soporte@rentiapropiedades.com</p>
                </div>
              </a>
            </Reveal>
            <div className="hidden sm:block w-px h-10 bg-white/10" />
            <Reveal delay={100}>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#ed6f28] flex-shrink-0" />
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-0.5">Soporte</p>
                  <p className="text-gray-200 text-sm font-medium">Lunes a viernes · 9 a 18 hs</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-white/5 px-6 py-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <img
              src="/logorentiacompletonaranjablanco.png"
              alt="Rentia"
              className="h-7 w-auto object-contain opacity-60 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/')}
            />
            <div className="flex items-center gap-6 text-xs text-gray-400">
              <a href="/terminos" className="hover:text-white transition-colors">Términos y condiciones</a>
              <a href="/privacidad" className="hover:text-white transition-colors">Privacidad</a>
              <span>© {new Date().getFullYear()} Rentia</span>
            </div>
            <a
              href="https://fedegr2.github.io/portfolio/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
              style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: '0.75rem', color: '#f0efe9', letterSpacing: '-0.3px' }}
            >
              hecho por fede<span style={{ color: '#ff6b2b' }}>.</span>dev
            </a>
          </div>
        </div>
      </footer>

      {/* ── WHATSAPP FLOATING BUTTON ── */}
      <a
        href="https://wa.me/5491135955223"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 z-50 group"
        aria-label="Contactar por WhatsApp"
      >
        <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-20" />
        <div className="relative w-14 h-14 bg-[#25D366] hover:bg-[#1ebe57] rounded-full flex items-center justify-center shadow-lg shadow-green-500/25 transition-all duration-300 group-hover:scale-110 group-hover:shadow-green-500/40">
          <WhatsAppIcon className="w-7 h-7 text-white" />
        </div>
      </a>

      {/* ── KEYFRAMES ── */}
      <style>{`
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes orbPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.08; }
          50%      { transform: translate(-50%, -50%) scale(1.15); opacity: 0.12; }
        }
        @keyframes particleFloat {
          0%, 100% { transform: translateY(0) translateX(0); }
          25%      { transform: translateY(-20px) translateX(10px); }
          50%      { transform: translateY(-10px) translateX(-10px); }
          75%      { transform: translateY(-30px) translateX(5px); }
        }
        @keyframes scrollDot {
          0%        { opacity: 1; transform: translateY(0); }
          100%      { opacity: 0; transform: translateY(10px); }
        }
        @keyframes ctaGlow {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50%      { opacity: 0.8; transform: scale(1.05); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .plan-shimmer::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: linear-gradient(90deg, transparent, rgba(237,111,40,0.06), transparent);
          background-size: 200% 100%;
          animation: shimmer 4s ease-in-out infinite;
          pointer-events: none;
        }
        .stat-number {
          background: linear-gradient(135deg, #f5a623 0%, #ed6f28 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          filter: drop-shadow(0 0 18px rgba(237,111,40,0.35));
        }
        .hero-badge-glow {
          animation: badgePulse 3s ease-in-out infinite;
        }
        @keyframes badgePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(237,111,40,0); }
          50%      { box-shadow: 0 0 20px 2px rgba(237,111,40,0.15); }
        }
        .hero-outline-text {
          -webkit-text-fill-color: transparent;
          -webkit-text-stroke: 2px #ed6f28;
          text-stroke: 2px #ed6f28;
        }
      `}</style>
    </div>
  );
}
